import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Tooltip,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AccountBalance as BudgetIcon,
} from '@mui/icons-material';
import { MainLayout } from '../components/MainLayout';
import { CategoryPicker } from '../components/CategoryPicker';
import {
  BudgetsService,
  type Budget,
  type BudgetVsActualItem,
  type BudgetVsActualResponse,
  type CreateBudgetRequest,
  type CreateBudgetItemRequest,
} from '../services/budgets-service';

export const BudgetsPage: React.FC = () => {
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [selectedBudget, setSelectedBudget] = useState<Budget | null>(null);
  const [vsActual, setVsActual] = useState<BudgetVsActualResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Dialogs
  const [budgetDialogOpen, setBudgetDialogOpen] = useState(false);
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [editingBudget, setEditingBudget] = useState<Budget | null>(null);

  // Forms
  const [budgetForm, setBudgetForm] = useState<CreateBudgetRequest>({
    name: '',
    period_type: 'monthly',
    start_date: new Date().toISOString().slice(0, 7) + '-01',
    end_date: '',
    notes: '',
  });
  const [itemForm, setItemForm] = useState<CreateBudgetItemRequest>({
    name: '',
    budgeted_amount: '',
    category_id: '',
    category_group_id: '',
    rollover: false,
  });

  const fetchBudgets = useCallback(async () => {
    try {
      setLoading(true);
      const res = await BudgetsService.listBudgets();
      setBudgets(res.data.budgets);
      if (res.data.budgets.length > 0 && !selectedBudget) {
        setSelectedBudget(res.data.budgets[0]);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load budgets');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchVsActual = useCallback(async () => {
    if (!selectedBudget) return;
    try {
      const res = await BudgetsService.getBudgetVsActual(selectedBudget.id);
      setVsActual(res.data);
    } catch (err: any) {
      console.error('Failed to load budget vs actual:', err);
    }
  }, [selectedBudget]);

  useEffect(() => {
    fetchBudgets();
  }, [fetchBudgets]);

  useEffect(() => {
    fetchVsActual();
  }, [fetchVsActual]);

  const handleCreateBudget = async () => {
    try {
      await BudgetsService.createBudget(budgetForm);
      setSuccess('Budget created');
      setBudgetDialogOpen(false);
      setBudgetForm({ name: '', period_type: 'monthly', start_date: '', end_date: '' });
      fetchBudgets();
    } catch (err: any) {
      setError(err.message || 'Failed to create budget');
    }
  };

  const handleUpdateBudget = async () => {
    if (!editingBudget) return;
    try {
      await BudgetsService.updateBudget(editingBudget.id, budgetForm);
      setSuccess('Budget updated');
      setBudgetDialogOpen(false);
      setEditingBudget(null);
      fetchBudgets();
    } catch (err: any) {
      setError(err.message || 'Failed to update budget');
    }
  };

  const handleDeleteBudget = async (id: string) => {
    if (!confirm('Delete this budget and all its items?')) return;
    try {
      await BudgetsService.deleteBudget(id);
      setSuccess('Budget deleted');
      if (selectedBudget?.id === id) setSelectedBudget(null);
      fetchBudgets();
    } catch (err: any) {
      setError(err.message || 'Failed to delete budget');
    }
  };

  const handleCreateItem = async () => {
    if (!selectedBudget) return;
    try {
      await BudgetsService.createBudgetItem(selectedBudget.id, itemForm);
      setSuccess('Budget item added');
      setItemDialogOpen(false);
      setItemForm({ name: '', budgeted_amount: '', category_id: '', rollover: false });
      fetchVsActual();
    } catch (err: any) {
      setError(err.message || 'Failed to create budget item');
    }
  };

  const handleDeleteItem = async (itemId: string) => {
    if (!selectedBudget || !confirm('Delete this budget item?')) return;
    try {
      await BudgetsService.deleteBudgetItem(selectedBudget.id, itemId);
      setSuccess('Budget item deleted');
      fetchVsActual();
    } catch (err: any) {
      setError(err.message || 'Failed to delete budget item');
    }
  };

  const openNewBudget = () => {
    setEditingBudget(null);
    const now = new Date();
    const startDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    const endMonth = now.getMonth() + 2 > 12 ? 1 : now.getMonth() + 2;
    const endYear = now.getMonth() + 2 > 12 ? now.getFullYear() + 1 : now.getFullYear();
    const lastDay = new Date(endYear, endMonth, 0).getDate();
    const endDate = `${endYear}-${String(endMonth).padStart(2, '0')}-${lastDay}`;
    setBudgetForm({
      name: `${now.toLocaleString('default', { month: 'long' })} ${now.getFullYear()} Budget`,
      period_type: 'monthly',
      start_date: startDate,
      end_date: endDate,
    });
    setBudgetDialogOpen(true);
  };

  const openEditBudget = (budget: Budget) => {
    setEditingBudget(budget);
    setBudgetForm({
      name: budget.name,
      period_type: budget.period_type,
      start_date: budget.start_date,
      end_date: budget.end_date,
      notes: budget.notes || '',
    });
    setBudgetDialogOpen(true);
  };

  const getProgressColor = (percent: number): 'success' | 'warning' | 'error' => {
    if (percent <= 75) return 'success';
    if (percent <= 90) return 'warning';
    return 'error';
  };

  if (loading) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Budgets
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Track spending against your budget goals
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openNewBudget}>
            New Budget
          </Button>
        </Box>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {budgets.length === 0 ? (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <BudgetIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No budgets yet
              </Typography>
              <Typography variant="body2" color="text.disabled" sx={{ mb: 2 }}>
                Create a budget to start tracking your spending
              </Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={openNewBudget}>
                Create Your First Budget
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Budget selector tabs */}
            <Tabs
              value={selectedBudget?.id || false}
              onChange={(_, id) => {
                const budget = budgets.find((b) => b.id === id);
                if (budget) setSelectedBudget(budget);
              }}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
            >
              {budgets.map((b) => (
                <Tab
                  key={b.id}
                  value={b.id}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {b.name}
                      {!b.is_active && <Chip label="Inactive" size="small" sx={{ height: 18, fontSize: '0.65rem' }} />}
                    </Box>
                  }
                />
              ))}
            </Tabs>

            {selectedBudget && (
              <>
                {/* Budget header card */}
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="h6" fontWeight={600}>
                          {selectedBudget.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(selectedBudget.start_date).toLocaleDateString()} — {new Date(selectedBudget.end_date).toLocaleDateString()}
                          {' · '}
                          <Chip
                            label={selectedBudget.period_type}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Edit budget">
                          <IconButton size="small" onClick={() => openEditBudget(selectedBudget)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete budget">
                          <IconButton size="small" color="error" onClick={() => handleDeleteBudget(selectedBudget.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    {/* Summary bar */}
                    {vsActual && (
                      <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2" color="text.secondary">
                            Total spent: ${parseFloat(vsActual.total_actual).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Budget: ${parseFloat(vsActual.total_budgeted).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(parseFloat(vsActual.total_percent), 100)}
                          color={getProgressColor(parseFloat(vsActual.total_percent))}
                          sx={{ height: 10, borderRadius: 5 }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          {vsActual.total_percent}% used · ${parseFloat(vsActual.total_remaining).toLocaleString(undefined, { minimumFractionDigits: 2 })} remaining
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>

                {/* Add item button */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                  <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setItemDialogOpen(true)}>
                    Add Budget Item
                  </Button>
                </Box>

                {/* Budget items with progress */}
                {vsActual && vsActual.items.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {vsActual.items.map((item) => {
                      const percent = parseFloat(item.percent_used);
                      const budgeted = parseFloat(item.budgeted_amount);
                      const actual = parseFloat(item.actual_amount);
                      const remaining = parseFloat(item.remaining_amount);

                      return (
                        <Card key={item.budget_item_id} variant="outlined">
                          <CardContent sx={{ pb: '12px !important' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {item.category_color && (
                                  <Box
                                    sx={{
                                      width: 10,
                                      height: 10,
                                      borderRadius: '50%',
                                      bgcolor: item.category_color,
                                    }}
                                  />
                                )}
                                <Typography fontWeight={600}>{item.name}</Typography>
                                {item.category_name && (
                                  <Chip
                                    label={item.category_name}
                                    size="small"
                                    sx={{
                                      height: 20,
                                      fontSize: '0.7rem',
                                      bgcolor: item.category_color ? `${item.category_color}15` : undefined,
                                      color: item.category_color || 'text.secondary',
                                    }}
                                  />
                                )}
                                {item.category_group_name && !item.category_name && (
                                  <Chip
                                    label={item.category_group_name}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                  />
                                )}
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" fontWeight={600}>
                                  ${actual.toLocaleString(undefined, { minimumFractionDigits: 2 })} / ${budgeted.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </Typography>
                                <Tooltip title="Delete item">
                                  <IconButton size="small" color="error" onClick={() => handleDeleteItem(item.budget_item_id)}>
                                    <DeleteIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(percent, 100)}
                              color={getProgressColor(percent)}
                              sx={{ height: 6, borderRadius: 3 }}
                            />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">
                                {percent.toFixed(1)}% used
                              </Typography>
                              <Typography
                                variant="caption"
                                color={remaining >= 0 ? 'success.main' : 'error.main'}
                                fontWeight={500}
                              >
                                {remaining >= 0 ? `$${remaining.toFixed(2)} left` : `$${Math.abs(remaining).toFixed(2)} over`}
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>
                ) : (
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="text.secondary">
                        No budget items yet. Add items to track spending by category.
                      </Typography>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </>
        )}

        {/* Budget Dialog */}
        <Dialog open={budgetDialogOpen} onClose={() => setBudgetDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{editingBudget ? 'Edit Budget' : 'New Budget'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField
                label="Name"
                value={budgetForm.name}
                onChange={(e) => setBudgetForm({ ...budgetForm, name: e.target.value })}
                fullWidth
                autoFocus
              />
              <FormControl fullWidth>
                <InputLabel>Period Type</InputLabel>
                <Select
                  value={budgetForm.period_type}
                  label="Period Type"
                  onChange={(e) => setBudgetForm({ ...budgetForm, period_type: e.target.value })}
                >
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="yearly">Yearly</MenuItem>
                  <MenuItem value="custom">Custom</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Start Date"
                type="date"
                value={budgetForm.start_date}
                onChange={(e) => setBudgetForm({ ...budgetForm, start_date: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="End Date"
                type="date"
                value={budgetForm.end_date}
                onChange={(e) => setBudgetForm({ ...budgetForm, end_date: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Notes (Optional)"
                value={budgetForm.notes || ''}
                onChange={(e) => setBudgetForm({ ...budgetForm, notes: e.target.value })}
                fullWidth
                multiline
                rows={2}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBudgetDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={editingBudget ? handleUpdateBudget : handleCreateBudget}
              disabled={!budgetForm.name || !budgetForm.start_date || !budgetForm.end_date}
            >
              {editingBudget ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Budget Item Dialog */}
        <Dialog open={itemDialogOpen} onClose={() => setItemDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add Budget Item</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField
                label="Name"
                value={itemForm.name}
                onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                fullWidth
                autoFocus
              />
              <TextField
                label="Budgeted Amount"
                type="number"
                value={itemForm.budgeted_amount}
                onChange={(e) => setItemForm({ ...itemForm, budgeted_amount: e.target.value })}
                fullWidth
                inputProps={{ step: '0.01', min: '0' }}
              />
              <CategoryPicker
                value={itemForm.category_id || null}
                onChange={(categoryId) =>
                  setItemForm({ ...itemForm, category_id: categoryId || '', category_group_id: '' })
                }
                label="Category"
                size="medium"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setItemDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleCreateItem}
              disabled={!itemForm.name || !itemForm.budgeted_amount || !itemForm.category_id}
            >
              Add
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </MainLayout>
  );
};
